import axios, { endPoints } from 'utils/api';

const SET_LIST = 'communityGroups/SET_LIST';
const SET_LOADING = 'communityGroups/SET_LOADING';
const SET_ERROR = 'communityGroups/SET_ERROR';
const UPSERT_GROUP = 'communityGroups/UPSERT_GROUP';

const INITIAL_STATE = {
    data: [],
    loading: false,
    error: null,
    meta: { total: 0, currentPage: 1, perPage: 50, lastPage: 1 }
};

function groupKey(group) {
    return group?.id ?? group?.publicId ?? group?.public_id;
}

export function normaliseGroup(value = {}) {
    const raw = value?.group || value?.data || value || {};
    const id = groupKey(raw);

    return {
        ...raw,
        id,
        publicId: raw.publicId ?? raw.public_id ?? id,
        name: raw.name ?? raw.title ?? '',
        image: raw.image ?? raw.imageUrl ?? raw.image_url ?? raw.coverImage ?? raw.cover_image ?? '',
        categories: raw.categories ?? raw.categoryGroups ?? raw.category_groups ?? [],
        isViolence: raw.isViolence ?? raw.is_violence ?? false
    };
}

export function normaliseGroupResponse(response) {
    const payload = response?.data ?? response ?? {};
    const nested = payload.data && !Array.isArray(payload.data) ? payload.data : payload;
    const rows = Array.isArray(nested)
        ? nested
        : Array.isArray(nested.data)
            ? nested.data
            : Array.isArray(nested.groups)
                ? nested.groups
                : [];
    const meta = nested.meta || nested;

    return {
        data: rows.map(normaliseGroup),
        meta: {
            total: Number(meta.total ?? 0),
            currentPage: Number(meta.currentPage ?? meta.current_page ?? 1),
            perPage: Number(meta.perPage ?? meta.per_page ?? 50),
            lastPage: Number(meta.lastPage ?? meta.last_page ?? 1)
        }
    };
}

export function buildCommunityGroupsQuery({ search = '', filter = 'all', page = 1, pageSize = 50 } = {}) {
    const query = {
        page,
        pageSize
    };

    if (search.trim()) query.search = search.trim();
    if (filter === 'tagged') query.isViolence = 1;
    if (filter === 'untagged') query.isViolence = 0;

    return query;
}

function errorMessage(error) {
    const body = error?.response?.data;

    return body?.message || error?.message || 'Could not load community groups.';
}

export const setCommunityGroups = (data, meta) => ({
    type: SET_LIST,
    data,
    meta
});

export const setCommunityGroupsLoading = (loading) => ({
    type: SET_LOADING,
    loading
});

export const setCommunityGroupsError = (error) => ({
    type: SET_ERROR,
    error
});

export const upsertCommunityGroup = (groupId, group) => ({
    type: UPSERT_GROUP,
    groupId,
    group
});

// eslint-disable-next-line import/no-anonymous-default-export
export default function(state = INITIAL_STATE, action) {
    switch (action.type) {
        case SET_LIST:
            return {
                ...state,
                data: action.data,
                meta: { ...state.meta, ...action.meta },
                error: null
            };

        case SET_LOADING:
            return { ...state, loading: action.loading };

        case SET_ERROR:
            return { ...state, error: action.error, loading: false };

        case UPSERT_GROUP: {
            const key = String(action.groupId);
            const found = state.data.some((row) => String(groupKey(row)) === key);
            const data = found
                ? state.data.map((row) => String(groupKey(row)) === key
                    ? { ...row, ...action.group }
                    : row)
                : state.data;

            return { ...state, data, error: null };
        }

        default:
            return state;
    }
}

export const fetchCommunityGroups = (params = {}) => async (dispatch) => {
    dispatch(setCommunityGroupsLoading(true));

    try {
        const response = await axios.get(endPoints.adminGroups, {
            params: buildCommunityGroupsQuery(params)
        });
        const result = normaliseGroupResponse(response);

        dispatch(setCommunityGroups(result.data, result.meta));

        return result;
    } catch (error) {
        dispatch(setCommunityGroupsError(errorMessage(error)));
        throw error;
    } finally {
        dispatch(setCommunityGroupsLoading(false));
    }
};

export const updateCommunityGroupViolence = (groupId, isViolence) => async (dispatch) => {
    try {
        const response = await axios.put(
            `${endPoints.adminGroups}/${encodeURIComponent(groupId)}/violence`,
            { isViolence: isViolence === true }
        );
        const group = normaliseGroup(response.data);
        const updated = groupKey(group) === undefined
            ? { ...group, id: groupId, publicId: groupId }
            : group;

        dispatch(upsertCommunityGroup(groupId, updated));

        return updated;
    } catch (error) {
        dispatch(setCommunityGroupsError(errorMessage(error)));
        throw error;
    }
};
